import * as React from "react";
import styles from "./MButtonCard.module.css";
import MBadge from "./MBadge";

export type MButtonCardType = "count" | "plain";
export type MButtonCardSize = "default" | "sm" | "small" | "large" | "vertical";

export interface MButtonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** e.g. 102 -> rendered as (102) for count type */
  count?: number | string;
  countVariant?: 'default' | 'badge';
  icon?: React.ReactNode;
  type?: MButtonCardType;
  size?: MButtonCardSize;
  disabled?: boolean;
  greyVariant?: boolean;
}

export const MButtonCard: React.FC<MButtonCardProps> = ({
  title,
  count,
  countVariant = 'default',
  icon,
  type = "count",
  size = "default",
  disabled = false,
  greyVariant = false,
  className,
  ...rest
}) => {
  const isCountType = type === "count";

  const renderIcon = () => {
    if (!icon) return null;
    return (
      <div className={styles.iconWrapper}>
        {React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement, {
              ...icon.props,
              width: 24,
              height: 24,
            })
          : icon}
      </div>
    );
  };

  // Large variant: horizontal layout with icon left, count right, label below
  if (size === "large") {
    return (
      <div
        className={[
          styles.buttonCard,
          styles.buttonCardLarge,
          disabled ? styles.buttonCardDisabled : '',
          greyVariant ? styles.greyVariant : '',
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      >
        <div className={styles.largeTopSection}>
          {renderIcon()}
          {count !== undefined && (
            <span className={styles.countNumberLarge}>{count}</span>
          )}
        </div>
        <div className={styles.largeBottomSection}>
          <span className={styles.labelLarge}>{title}</span>
        </div>
      </div>
    );
  }

  // Small variant: vertical layout with icon+count on top, label below
  if (size === "small") {
    return (
      <div
        className={[
          styles.buttonCard,
          styles.buttonCardSmall,
          disabled ? styles.buttonCardDisabled : '',
          greyVariant ? styles.greyVariant : '',
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      >
        <div className={styles.topSection}>
          {renderIcon()}
          {count !== undefined && (
            <span className={styles.countNumber}>{count}</span>
          )}
        </div>
        <div className={styles.bottomSection}>
          <span className={styles.labelSmall}>{title}</span>
        </div>
      </div>
    );
  }

  // Vertical variant: similar to small but with specific styling for member detail page
  if (size === "vertical") {
    return (
      <div
        className={[
          styles.buttonCard,
          styles.buttonCardVertical,
          disabled ? styles.buttonCardDisabled : '',
          greyVariant ? styles.greyVariant : '',
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      >
        <div className={styles.verticalContent}>
          {renderIcon()}
          {count !== undefined && (
            <span className={styles.countNumberVertical}>{count}</span>
          )}
        </div>
        <span className={styles.labelVertical}>{title}</span>
      </div>
    );
  }

  // Horizontal layout (title + optional count left, icon pill right)
  // Used for:
  // - type="count" with size="default" (primary spec)
  // - any other combination except plain+sm
  if (!(size === "sm" && type === "plain")) {
    return (
      <div
        className={[
          styles.buttonCard,
          styles[`buttonCard--size-${size}`],
          styles[`buttonCard--type-${type}`],
          disabled ? styles.buttonCardDisabled : '',
          greyVariant ? styles.greyVariant : '',
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      >
        <div className={styles.leftContent}>
          <div className="body-base-semibold">
            {isCountType && countVariant === 'badge' && typeof count !== "undefined" ? (
              <div className={styles.titleRow}>
                <span className={styles.title}>{title}</span>
                <MBadge variant="info">{count}</MBadge>
              </div>
            ) : (
              <span className={styles.title}>{title}</span>
            )}
          </div>
          {isCountType && countVariant !== 'badge' && typeof count !== "undefined" && (
            <div className="body-xs-medium">
              <span className={styles.count}>({count})</span>
            </div>
          )}
        </div>

        {renderIcon()}
      </div>
    );
  }

  // Small plain: vertical layout (icon on top, title below)
  return (
    <div
      className={[
        styles.buttonCard,
        styles[`buttonCard--size-${size}`],
        styles[`buttonCard--type-${type}`],
        styles.buttonCardSmall,
        disabled ? styles.buttonCardDisabled : '',
        greyVariant ? styles.greyVariant : '',
        countVariant === 'badge' ? styles.badgeVariant : '',
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {renderIcon()}

      <div className={styles.smallTextBlock}>
        <div className="body-xs-semibold">
          <span className={styles.titleSmall}>{title}</span>
        </div>
      </div>
    </div>
  );
};

export default MButtonCard;
